import { InvalidVaultEntrySignatureError } from '../errors/invalid-vault-entry-signature-error';
import { CryptoService } from '../services/crypto-service';
import { InvalidDateVaultEntryError } from '../errors/invalid-date-vault-entry-error';
import { InvalidVaultEntryError } from '../errors/invalid-vault-entry-error';
import { addMinutes, areEqualUint8Array, arrayBufferToBase64, combineUint8Arrays, getUnsafeDateNow } from '../utils/utils';
import { ValidationContext } from './validation-context';
import { VaultOperation } from './vault-operation';
import { UserKeyType } from './user-key-type';
import { RevokedPublicKeyError } from '../errors/revoked-public-key-error';
import { InvalidSignatureError } from '../errors/invalid-signature-error';
import { getVaultEntryBytesToBeSigned } from './vault-utils';
export class Vault {
    constructor(entries) {
        this.entries = [];
        this.supportedVaultEntryVersion = 1;
        this.minimumDate = new Date(Date.UTC(2021, 9, 1, 0, 0, 0));
        this.timeSkew = 5; // in minutes
        this.cryptoService = new CryptoService();
        entries.sort((a, b) => a.id - b.id);
        this.entries = entries;
    }
    static async Create(entries, validateFromEntry = 0) {
        const vault = new Vault(entries);
        await vault.validate(validateFromEntry);
        return vault;
    }
    get length() {
        return this.entries.length;
    }
    get effectiveEntries() {
        return this.entries.filter(entry => entry.operation === VaultOperation.AddKey && !this.removedPublicKeys.some(removedKey => areEqualUint8Array(removedKey, entry.requestorPublicKey)));
    }
    get effectiveSignatureEntries() {
        return this.effectiveEntries.filter(entry => this.isForSignature(entry.keyType));
    }
    effectiveSignatureEntriesAt(at) {
        const removedPublicKeys = this.removedPublicKeysAt(at);
        return this.entries
            .filter(entry => entry.addedAt <= at)
            .filter(entry => this.isForSignature(entry.keyType))
            .filter(entry => entry.operation === VaultOperation.AddKey &&
            !removedPublicKeys.some(removedKey => areEqualUint8Array(removedKey, entry.requestorPublicKey)));
    }
    hasEffectivePasswordKeyFor(userId) {
        return this.effectiveSignatureEntries.filter(entry => entry.userId === userId && entry.keyType === UserKeyType.Password).length > 0;
    }
    hasEffectiveCompanyKey() {
        return this.length === 1 && this.entries[0].keyType === UserKeyType.Company;
    }
    isForSignature(keyType) {
        return keyType === UserKeyType.Password || keyType === UserKeyType.Company;
    }
    isEmpty() {
        return this.entries.length === 0;
    }
    isKeyActive(publicKey) {
        return !this.removedPublicKeys.some(removedKey => areEqualUint8Array(removedKey, new Uint8Array(publicKey)));
    }
    findById(id) {
        const entry = this.entries.find(entry => entry.id === id);
        if (entry) {
            return entry;
        }
        throw new RangeError('Id not found.');
    }
    findByPublicKey(publicKey) {
        return this.effectiveSignatureEntries.find(entry => areEqualUint8Array(entry.requestorPublicKey, publicKey));
    }
    getEffectiveRecoveryEntries(userId) {
        return this.effectiveEntries.filter(entry => entry.keyType === UserKeyType.Recovery && entry.userId === userId);
    }
    getLastEntry() {
        return this.entries[this.entries.length - 1];
    }
    get removedPublicKeys() {
        return this.entries
            .filter(entry => entry.operation === VaultOperation.RemoveKey)
            .map(entry => entry.requestorPublicKey);
    }
    removedPublicKeysAt(at) {
        return this.entries
            .filter(entry => entry.addedAt <= at)
            .filter(entry => entry.operation === VaultOperation.RemoveKey)
            .map(entry => entry.requestorPublicKey);
    }
    async validate(validateFromEntry) {
        if (this.isEmpty()) {
            return;
        }
        const validationContext = new ValidationContext(this.entries, validateFromEntry);
        for (const entry of this.entries.slice(validateFromEntry)) {
            await this.validateEntry(entry, validationContext);
            validationContext.expectedId++;
            validationContext.entries.push(entry);
        }
    }
    async validateEntry(entry, validationContext) {
        if (entry.version !== this.supportedVaultEntryVersion) {
            throw new InvalidVaultEntryError('Unsupported version.', entry);
        }
        if (entry.id !== validationContext.expectedId) {
            throw new InvalidVaultEntryError("Entry id mismatch.", entry);
        }
        if (entry.addedAt < this.minimumDate || entry.addedAt > addMinutes(getUnsafeDateNow(), this.timeSkew)) {
            throw new InvalidDateVaultEntryError(entry);
        }
        if (entry.keyType !== UserKeyType.Password && entry.keyType !== UserKeyType.Recovery && entry.keyType !== UserKeyType.Company) {
            throw new InvalidVaultEntryError(`Invalid KeyType: ${entry.keyType}.`, entry);
        }
        if (entry.keyType === UserKeyType.Recovery && this.entries[entry.verifiedById].userId !== entry.userId && entry.operation === VaultOperation.AddKey) {
            throw new InvalidVaultEntryError("The recovery key can only be created for own use.", entry);
        }
        const publicKeyBase64Encoded = arrayBufferToBase64(entry.requestorPublicKey.buffer);
        switch (entry.operation) {
            case VaultOperation.AddKey: {
                if (!validationContext.idByPublicKey.has(publicKeyBase64Encoded)) {
                    validationContext.idByPublicKey.set(publicKeyBase64Encoded, entry.id);
                }
                else {
                    throw new InvalidVaultEntryError("Public key already exists in the vault.", entry);
                }
                break;
            }
            case VaultOperation.RemoveKey: {
                if (validationContext.idByPublicKey.has(publicKeyBase64Encoded)) {
                    const revokedId = validationContext.idByPublicKey.get(publicKeyBase64Encoded);
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    if (!validationContext.revokedIds.has(revokedId)) {
                        validationContext.revokedIds.add(revokedId);
                    }
                    else {
                        throw new InvalidVaultEntryError("Public key already revoked in the vault.", entry);
                    }
                }
                else {
                    throw new InvalidVaultEntryError("Revoked public key cannot be found in the vault.", entry);
                }
                break;
            }
            default:
                throw new InvalidVaultEntryError("Unsupported operation.", entry);
        }
        if (entry.id === 0) {
            await this.checkRootEntryRules(entry);
        }
        else {
            await this.checkNonRootEntryRules(entry, validationContext);
        }
        const entrySignaturePayload = getVaultEntryBytesToBeSigned(entry);
        const verifierEntry = entry.id === entry.verifiedById ? entry : this.entries[entry.verifiedById];
        await this.verifyEntrySignature(entrySignaturePayload, verifierEntry.requestorPublicKey, entry.verifierSignature, 'verifierSignature', entry);
    }
    async checkRootEntryRules(entry) {
        // rootEntry should be verified by itself
        if (entry.verifiedById !== entry.id) {
            throw new InvalidVaultEntryError('Root entry\'s verifiedById is invalid.', entry);
        }
        // rootEntry's LastSeenId should be its own Id
        if (entry.lastSeenId !== entry.id) {
            throw new InvalidVaultEntryError('Root entry\'s lastSeenId is invalid.', entry);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (!areEqualUint8Array(entry.verifierSignature, entry.requestorSignature)) {
            throw new InvalidVaultEntrySignatureError('RequestorSignature', entry);
        }
        if (!areEqualUint8Array(entry.verifierSignature, entry.blockchainSignature)) {
            throw new InvalidVaultEntrySignatureError('BlockchainSignature', entry);
        }
        // this supposed to never happen, because only Add and Remove are supported and Remove check would fail earlier because we don't have any key to remove
        if (entry.operation !== VaultOperation.AddKey || (entry.keyType != UserKeyType.Password && entry.keyType != UserKeyType.Company)) {
            throw new InvalidVaultEntryError('Root entry must have an "Password|Company.AddKey" operation.', entry);
        }
    }
    async checkNonRootEntryRules(entry, validationContext) {
        const previousEntry = this.entries[entry.id - 1];
        if (previousEntry.addedAt >= entry.addedAt) {
            throw new InvalidDateVaultEntryError(entry);
        }
        if (entry.lastSeenId < 0 || entry.lastSeenId >= entry.id) {
            throw new InvalidVaultEntryError('LastSeenId is invalid.', entry);
        }
        if (entry.verifiedById < 0 || entry.verifiedById >= entry.id) {
            throw new InvalidVaultEntryError('VerifiedById is invalid.', entry);
        }
        if (validationContext.revokedIds.has(entry.verifiedById)) {
            throw new RevokedPublicKeyError(entry);
        }
        const verifyingEntry = this.entries[entry.verifiedById];
        if (verifyingEntry.keyType === UserKeyType.Recovery && (verifyingEntry.userId !== entry.userId || entry.operation !== VaultOperation.AddKey)) {
            throw new InvalidVaultEntryError("The recovery key can only be used for password recovery.", entry);
        }
        if (entry.operation === VaultOperation.AddKey && entry.userId !== verifyingEntry.userId && verifyingEntry.keyType !== UserKeyType.Company) {
            const lastSeenEntry = this.entries[entry.lastSeenId];
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            await this.verifyEntrySignature(lastSeenEntry.blockchainSignature, entry.requestorPublicKey, entry.requestorSignature, 'requestorSignature', entry);
        }
        if (entry.operation == VaultOperation.AddKey && entry.keyType === UserKeyType.Company) {
            throw new InvalidVaultEntryError("Non root entry mustn't have a Company.AddKey operation.", entry);
        }
        if (entry.operation === VaultOperation.RemoveKey) {
            const publicKeyBase64Encoded = arrayBufferToBase64(entry.requestorPublicKey.buffer);
            if (validationContext.idByPublicKey.has(publicKeyBase64Encoded)) {
                const revokedId = validationContext.idByPublicKey.get(publicKeyBase64Encoded);
                const revokedEntry = this.entries.find(r => r.id === revokedId);
                if (revokedEntry && revokedEntry.keyType !== entry.keyType) {
                    throw new InvalidVaultEntryError("The keytypes must match.", entry);
                }
            }
        }
        const blockchainSignaturePayload = combineUint8Arrays(previousEntry.blockchainSignature, entry.requestorSignature ? entry.requestorSignature : new Uint8Array(0), entry.verifierSignature);
        await this.verifyEntrySignature(blockchainSignaturePayload, verifyingEntry.requestorPublicKey, entry.blockchainSignature, 'blockchainSignature', entry);
    }
    async verifyEntrySignature(payload, publicKeyBytes, signature, signatureName, entry) {
        const publicKey = await this.cryptoService.importPublicKey(publicKeyBytes.buffer);
        if (!(await this.cryptoService.verifySignature(publicKey, payload, signature))) {
            throw new InvalidVaultEntrySignatureError(signatureName, entry);
        }
    }
    async verifySignatureByIdAt(payload, signature, signatureName, at, entryId) {
        const entry = this.effectiveSignatureEntriesAt(at).find(e => e.id === entryId);
        if (!entry) {
            throw new InvalidSignatureError(`Vault entry not found. '${signatureName}' is invalid for '${entryId}' keyvault entry id`);
        }
        const publicKey = await this.cryptoService.importPublicKey(entry.requestorPublicKey.buffer);
        if (!(await this.cryptoService.verifySignature(publicKey, payload, signature))) {
            throw new InvalidSignatureError(`'${signatureName}' is invalid for '${entryId}' keyvault entry id`);
        }
    }
    async verifySignature(payload, signature) {
        for (const entry of this.effectiveSignatureEntries) {
            const publicKey = await this.cryptoService.importPublicKey(entry.requestorPublicKey.buffer);
            if (await this.cryptoService.verifySignature(publicKey, payload, signature)) {
                return;
            }
        }
        throw new InvalidSignatureError();
    }
    async verifyRecoveryFileSignature(payload, signature, userId) {
        for (const entry of this.getEffectiveRecoveryEntries(userId)) {
            const publicKey = await this.cryptoService.importPublicKey(entry.requestorPublicKey.buffer);
            if (await this.cryptoService.verifySignature(publicKey, payload, signature)) {
                return entry;
            }
        }
        throw new InvalidSignatureError();
    }
    /** @internal */
    isCompanyKeyUpgradeOverdue() {
        if (this.hasEffectiveCompanyKey()) {
            return this.isMoreThanAMonthApart(getUnsafeDateNow(), this.entries[0].addedAt);
        }
        return false;
    }
    isMoreThanAMonthApart(date1, date2) {
        const oneMonth = 30 * 24 * 60 * 60 * 1000; // one month in milliseconds
        return Math.abs(date1.getTime() - date2.getTime()) >= oneMonth;
    }
}
